import {
  AloftArea,
  AloftDate,
  AloftPrice,
  CompType,
  LatitudeLongitude,
  PriorSales,
  PropertyIdentification,
  RESOMedia,
  RecommendedCompsStatistics,
  SubjectData,
  addressToString,
  formatLongRevisionType,
  formatPropertyType,
  isDefined,
  orUndefined,
  schAloftDate,
  schAloftPrice,
  schAloftTime,
} from '@aloft/toolkit-shared';

import { useMemo } from 'react';

import { createCircleCoordinates } from '../../../components/RecommendedCompSelection/hooks/createCircleCoordinates.ts';
import { useRecommendedProperties } from '../../../components/RecommendedCompSelection/hooks/useRecommendedProperties.ts';
import { useToolkitContext } from '../../../hooks/api/toolkitContext.ts';
import { useStreetViewStaticUrl } from '../../../hooks/googleMaps/useStreetViewStaticUrl.ts';
import { selectFullNameWithDefault } from '../../../redux/features/selectors/accountSelectors.ts';
import { selectCompAdjustments } from '../../../redux/features/selectors/adjustmentSelectors.ts';
import { selectAllCostApproachExportData } from '../../../redux/features/selectors/commentarySelectors/selectAllCostApproachExportData.ts';
import { selectCommentaryV2 } from '../../../redux/features/selectors/commentarySelectors/selectCommentary.ts';
import { selectCurrentMarketAggregateData } from '../../../redux/features/selectors/commentarySelectors/selectCurrentMarketAggregateData.ts';
import { selectMarketAdjustmentsCommentaryData } from '../../../redux/features/selectors/commentarySelectors/selectMarketAdjustmentsCommentary.ts';
import { selectCostNewByCategories } from '../../../redux/features/selectors/costSelectors/selectCostNewByCategory.ts';
import {
  selectDepreciations,
  selectDepreciationsByCategories,
} from '../../../redux/features/selectors/depreciationSelectors.ts';
import { selectOpinionOfSiteValuePrice } from '../../../redux/features/selectors/opinionOfSiteValueSelectors.ts';
import {
  selectAdjustmentSettings,
  selectAppraisalReportDisclaimer,
  selectCompSequence,
  selectCompTypes,
  selectEffectiveYear,
  selectIsArvOrder,
  selectOrder,
  selectOrderFeatures,
  selectSalesComparisonApproach,
} from '../../../redux/features/selectors/orderSelectors.ts';
import { selectPriorSales } from '../../../redux/features/selectors/priorSalesSelectors.ts';
import { selectManualAdjustmentsEntities } from '../../../redux/features/selectors/propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectPropertyDataEntities } from '../../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import {
  selectAllAdjustmentsTableData,
  selectAllArvAdjustmentsTableData,
} from '../../../redux/features/selectors/selectAllAdjustmentsTableData.ts';
import { selectCostNews } from '../../../redux/features/selectors/selectCostNew.ts';
import { selectImprovementsCosts } from '../../../redux/features/selectors/selectImprovementsCost.ts';
import {
  selectOrderHasExternalDepreciation,
  selectOrderHasFunctionalDepreciation,
} from '../../../redux/features/selectors/selectOrderDepreciation.ts';
import { selectPropertyValues } from '../../../redux/features/selectors/selectPropertyValue.ts';
import { selectReconciliation } from '../../../redux/features/selectors/selectReconciliation.ts';
import {
  selectAivReconciliationData,
  selectArvReconciliationData,
} from '../../../redux/features/selectors/selectReconciliationData.ts';
import { selectSiteValues } from '../../../redux/features/selectors/selectSiteValue.ts';
import { selectSubjectPropertyType } from '../../../redux/features/selectors/selectSubjectPropertyType.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import {
  CompPropertyDataState,
  RootState,
  SubjectPropertyDataState,
} from '../../../redux/types.ts';
import { getMapboxStaticURL } from '../../../utils/getMapboxStaticUrl.ts';
import { useAlerts } from '../../referenceView/AlertsSheet/hooks/useAlerts.tsx';
import { useTkv2SiteValueChartsParams } from '../../referenceView/utils/useTkv2SiteValueChartsParams.ts';
import { valueTypeLabels } from '../../tkv2PDF/constants.ts';
import { getSubjectPropertyImageUrl } from '../../tkv2PDF/utils/utils.ts';
import { buildCostApproachData } from '../builders/buildCostApproachData.ts';
import { buildReconciliationData } from '../builders/buildReconciliationData.ts';
import type { AppraisalReportCoreData } from '../types.ts';
import { useFetchEntityPhotos } from '../utils/index.ts';
import { useTransformSalesComparisonData } from '../utils/transformSalesComparison.ts';

/**
 * Core hook that fetches and processes all data needed for appraisal reports
 * (both PDF and API export).
 *
 * This hook consolidates all Redux selectors, data fetching, and transformations
 * into a single source of truth for appraisal report data.
 */
export const useAppraisalReportData = (): AppraisalReportCoreData => {
  // Redux selectors - Order & basic info
  const order = useAppSelector(selectOrder);
  const orderFeatures = useAppSelector(selectOrderFeatures);
  const compSequence = useAppSelector(selectCompSequence);
  const hasArvOption = useAppSelector(selectIsArvOrder);
  const salesComparisonApproach = useAppSelector(selectSalesComparisonApproach);
  const effectiveYear = useAppSelector(selectEffectiveYear);
  const appraisalReportDisclaimer = useAppSelector(selectAppraisalReportDisclaimer);

  // Redux selectors - Property data
  const propertyDataEntities = useAppSelector(selectPropertyDataEntities);
  const compTypes = useAppSelector(selectCompTypes) as Record<string, CompType | undefined>;

  // Redux selectors - Adjustments
  const adjustments = useAppSelector(selectCompAdjustments);
  const adjustmentSettings = useAppSelector(selectAdjustmentSettings);
  const manualAdjustmentsEntities = useAppSelector(selectManualAdjustmentsEntities);
  const adjustmentsTableData = useAppSelector(selectAllAdjustmentsTableData);
  const arvAdjustmentsTableData = useAppSelector(selectAllArvAdjustmentsTableData);

  // Redux selectors - Commentary
  const commentaryV2 = useAppSelector(selectCommentaryV2);
  const dateOfSaleChartParams = useAppSelector(selectMarketAdjustmentsCommentaryData)?.chart;

  // Redux selectors - Cost approach & depreciation
  const costApproachExportData = useAppSelector(selectAllCostApproachExportData);
  const arvCostApproachExportData = useAppSelector(selectAllCostApproachExportData);
  const siteValues = useAppSelector(selectSiteValues);
  const improvements = useAppSelector(selectImprovementsCosts);
  const costNews = useAppSelector(selectCostNews);
  const depreciations = useAppSelector(selectDepreciations);
  const indicatedCostValues = useAppSelector(selectPropertyValues);
  const costNewsByCategories = useAppSelector(selectCostNewByCategories);
  const depreciationsByCategories = useAppSelector(selectDepreciationsByCategories);
  const hasFunctionalDepreciation = useAppSelector(selectOrderHasFunctionalDepreciation);
  const hasExternalDepreciation = useAppSelector(selectOrderHasExternalDepreciation);
  const opinionOfSiteValue = useAppSelector(selectOpinionOfSiteValuePrice);
  const subjectPropertyType = useAppSelector(selectSubjectPropertyType);

  // Redux selectors - Reconciliation
  const reconciliation = useAppSelector(selectReconciliation);
  const aivReconciliationSummarizedValues = useAppSelector(selectAivReconciliationData);
  const arvReconciliationSummarizedValues = useAppSelector(selectArvReconciliationData);

  // Redux selectors - Miscellaneous
  const appraiserName = useAppSelector(selectFullNameWithDefault);
  const priorSalesState = useAppSelector(selectPriorSales);
  const marketAggregateData = useAppSelector(selectCurrentMarketAggregateData);

  // Hooks
  const { fetcherOptions } = useToolkitContext();
  const siteValueChartParams = useTkv2SiteValueChartsParams();
  const alerts = useAlerts();
  const { data } = useRecommendedProperties({ orderID: order.id });

  // Extract subject data
  const subject: SubjectData | undefined = (
    propertyDataEntities?.subject as SubjectPropertyDataState
  )?.subjectData;

  const {
    address: subjectAddressRaw,
    zoning,
    zoningCompliance,
    parcelNumber,
    hoa,
    hoaFeeAmount,
    pud,
    locationType,
    propertyInterest,
    propertyInterestOther,
    hoaFeeFrequency,
    legalDescription,
    coordinates,
  } = subject?.editableData?.propertyIdentification as PropertyIdentification;

  const propertyInterestValue =
    propertyInterest === 'Other' ? propertyInterestOther : propertyInterest;

  const appraisalType = ((order.appraisalType as string) ?? 'Appraisal')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

  const subjectAddress = orUndefined(addressToString)(subjectAddressRaw);

  const effectiveDate = (
    orUndefined(schAloftDate.parse)(order.effectiveDate) ?? AloftDate.today()
  )?.format('MM/dd/yyyy');

  const inspectionDate = orUndefined(schAloftDate.parse)(order.inspectionDate)?.format(
    'MM/dd/yyyy',
  );

  // Entity media URLs
  const entityMediaURLs: Record<string, Required<RESOMedia>[]> = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(propertyDataEntities)
          .map(([id, propertyDataEntity]) => {
            if (!propertyDataEntity) return [id, undefined];

            const propertyData =
              id === 'subject'
                ? (propertyDataEntity as SubjectPropertyDataState).subjectData
                : (propertyDataEntity as CompPropertyDataState).compData;

            const userUploadedBlobID =
              propertyData.editableData.propertyIdentification.userUploadedPhotoBlobId;

            const media =
              propertyData?.readonlyData.backupListingData?.listing.Media?.filter(
                (item): item is Required<RESOMedia> => isDefined(item.blobID),
              ) ?? [];

            if (userUploadedBlobID) {
              media.unshift({ MediaURL: 'user-uploaded-blob', blobID: userUploadedBlobID });
            }

            return [id, media];
          })
          .filter(([_, media]) => media && media.length > 0) as [string, Required<RESOMedia>[]][],
      ),
    [propertyDataEntities],
  );

  // Fetch entity photos
  const entityListingPhotos = useFetchEntityPhotos(entityMediaURLs, fetcherOptions);

  // Sales comparable photos data
  const salesComparablePhotosData = useMemo(
    () =>
      compSequence
        .map((id) => {
          if (!propertyDataEntities[id]) return undefined;

          const {
            compType,
            editableData: { propertyIdentification, transaction },
          } = (propertyDataEntities[id] as CompPropertyDataState).compData;

          const salePrice = orUndefined(schAloftPrice.parse)(transaction.price);
          const listPrice = orUndefined(schAloftPrice.parse)(transaction.originalListPrice);

          return {
            id,
            address: addressToString(propertyIdentification.address),
            compType,
            price: transaction.status === 'SettledSale' ? salePrice : listPrice,
            photos: entityListingPhotos[id] ?? [],
            transactionText: transaction.status === 'SettledSale' ? 'Sale Price' : 'List Price',
          };
        })
        .filter(isDefined),
    [compSequence, propertyDataEntities, entityListingPhotos],
  );

  // Subject property images
  const mlsPhotoUrl = getSubjectPropertyImageUrl(subject);
  const streetViewStaticUrl = useStreetViewStaticUrl(!mlsPhotoUrl, subjectAddress, coordinates);
  const satelliteViewStaticUrl = coordinates
    ? getMapboxStaticURL(coordinates, '400x400', 'satellite')
    : undefined;

  const subjectPropertyImage = [
    entityListingPhotos?.subject?.[0],
    mlsPhotoUrl,
    streetViewStaticUrl,
  ].filter(isDefined)[0];

  const shortPropertyType = String(formatPropertyType(subjectPropertyType));

  let hoaFees = orUndefined(schAloftPrice.parse)(hoaFeeAmount)?.toString(true);
  if (hoaFees) hoaFees = hoaFeeFrequency ? `${hoaFees} (${hoaFeeFrequency})` : hoaFees;

  const pudDisplay = pud ? 'Yes' : undefined;

  // Transform sales comparison data
  const {
    subjectSalesComparisonData,
    subjectArvSalesComparisonData,
    comparablesSalesComparisonData,
  } = useTransformSalesComparisonData(
    subject,
    compSequence,
    propertyDataEntities,
    adjustments,
    manualAdjustmentsEntities,
    coordinates,
    entityListingPhotos,
    effectiveYear ? parseInt(effectiveYear, 10) : undefined,
    hasArvOption,
    reconciliation,
    orderFeatures,
  );

  // Helper functions for cost approach data
  const selectSubjectData = (
    selector: (state: RootState, key: string) => AloftPrice | AloftArea | undefined | null,
  ): AloftPrice | AloftArea | undefined | null =>
    useAppSelector((state: RootState) => selector(state, 'subject'));

  const selectArvSubjectData = (
    selector: (state: RootState, key: string) => AloftPrice | AloftArea | undefined | null,
  ): AloftPrice | AloftArea | undefined | null =>
    useAppSelector((state: RootState) => selector(state, 'subjectArv'));

  // Build cost approach data
  const costApproachData = buildCostApproachData(
    selectSubjectData,
    selectArvSubjectData,
    costApproachExportData,
    arvCostApproachExportData,
    opinionOfSiteValue,
    hasArvOption,
  );

  // Build reconciliation data
  const reconciliationPageData = buildReconciliationData(
    commentaryV2,
    aivReconciliationSummarizedValues,
    arvReconciliationSummarizedValues,
    hasArvOption,
  );

  // Comps for maps
  const compsForMaps = compSequence.reduce((acc, compId) => {
    if (propertyDataEntities[compId]) {
      const { id, compData } = propertyDataEntities[compId] as CompPropertyDataState;

      acc.push({
        id,
        address: compData.editableData.propertyIdentification.address.AddressLineText,
        latitude: compData.editableData.propertyIdentification.coordinates?.latitude ?? 0,
        longitude: compData.editableData.propertyIdentification.coordinates?.longitude ?? 0,
        mlsStatus: compData.editableData.transaction.status,
        saleDate: compData.editableData.transaction.saleDate,
      });
    }

    return acc;
  }, [] as any[]);

  const subjectCoordinates = {
    latitude: subject.editableData.propertyIdentification.coordinates?.latitude ?? 0,
    longitude: subject.editableData.propertyIdentification.coordinates?.longitude ?? 0,
  };

  const signedAt = orUndefined(schAloftTime.parse)(order.signedAt)?.toAloftDate().toIsoString();

  // Search criteria & map coordinates
  type SearchCriteria = {
    coordinates?: LatitudeLongitude[];
    radius?: number;
  };

  const searchCriteria: SearchCriteria =
    data?.body?.output && typeof data.body.output === 'object' && !('error' in data.body.output)
      ? (data.body.output as { searchCriteria: SearchCriteria }).searchCriteria
      : {};

  let mapBoxCoordinates: Array<{ latitude: number; longitude: number }> | undefined;

  if (searchCriteria) {
    if (Array.isArray(searchCriteria.coordinates) && searchCriteria.coordinates.length > 0) {
      mapBoxCoordinates = searchCriteria.coordinates;
    } else if (searchCriteria.radius) {
      mapBoxCoordinates = createCircleCoordinates(
        subjectCoordinates,
        searchCriteria.radius * 1000,
        64,
      )
        .map(([lon, lat]) => ({ longitude: lon, latitude: lat }))
        .filter(
          (coord): coord is { latitude: number; longitude: number } =>
            coord.latitude !== undefined && coord.longitude !== undefined,
        );
    }
  }

  let mapStatistics: RecommendedCompsStatistics | undefined;
  if (
    (data?.source === 'constellation1' || data?.source === 'abode') &&
    data?.body?.status === 'found'
  ) {
    mapStatistics = data.body?.output?.statistics;
  }

  // Prior sales
  const priorSales = Object.fromEntries(
    [
      priorSalesState.subject && ['subject', priorSalesState.subject],
      ...compSequence
        .filter((id) => priorSalesState[id])
        .map((id) => priorSalesState[id] && [id, priorSalesState[id]]),
    ].filter(Boolean) as [string, PriorSales][],
  );

  // Reconciliation values
  const {
    includeTotalCostToCure,
    opinionOfMarketValue,
    totalCostToCure,
    totalRenovationBudget,
    opinionOfAfterRepairValue,
    includeOpinionOfAfterRepairValue,
    includeTotalRenovationBudget,
  } = reconciliation;

  const { revisionData } = order;
  const revisionType = revisionData ? formatLongRevisionType(revisionData.revisionType) : undefined;

  const { CountyOrParish, CountyOrParishFIPS } =
    subject?.readonlyData.backupListingData?.listing ?? {};

  // ROI calculation
  const estimatedROI =
    hasArvOption && opinionOfAfterRepairValue && opinionOfMarketValue
      ? (() => {
          const arv = schAloftPrice.parse(opinionOfAfterRepairValue);
          const aiv = schAloftPrice.parse(opinionOfMarketValue);
          const budget = orUndefined(schAloftPrice.parse)(totalRenovationBudget) ?? AloftPrice.zero;

          return arv.sub(aiv).sub(budget);
        })()
      : undefined;

  const hasNegativeROI = estimatedROI?.le(AloftPrice.zero);

  const valueType = reconciliation.valueTypes?.map((type) => valueTypeLabels[type]).join(' & ');

  // Return all core data
  return {
    // Order & basic info
    order,
    orderId: order.id,
    subjectAddress,
    effectiveDate,
    inspectionDate,
    effectiveYear,
    appraisalType,
    shortPropertyType,
    propertyType: subjectPropertyType ?? 'singleFamilyResidence',
    appraisalReportDisclaimer,
    salesComparisonApproach: order.salesComparisonApproach,
    includeCostApproach: order.includeCostApproach,
    isInternalValuation: order.isInternalValuation ?? false,

    // Subject property
    subject,
    subjectAddressRaw,
    subjectCoordinates,
    subjectPropertyImage,

    // Property identification
    zoning,
    zoningCompliance,
    parcelNumber,
    propertyInterestValue,
    hoa,
    hoaFeeAmount,
    hoaFeeFrequency,
    hoaFees,
    pud,
    pudDisplay,
    locationType,
    legalDescription,
    coordinates,

    // Comparables
    compSequence,
    compTypes,

    // Sales comparison data
    subjectSalesComparisonData,
    subjectArvSalesComparisonData,
    comparablesSalesComparisonData,
    salesComparablePhotosData,

    // Photos
    entityListingPhotos,

    // Property data
    propertyDataEntities,

    // Adjustments
    adjustments,
    adjustmentSettings,
    manualAdjustmentsEntities,
    adjustmentsTableData,
    arvAdjustmentsTableData,

    // Commentary
    commentaryV2,
    dateOfSaleChartParams,

    // Cost approach
    costApproachData,
    costApproachExportData,
    arvCostApproachExportData,
    siteValues,
    improvements,
    costNews,
    depreciations,
    indicatedCostValues,
    costNewsByCategories,
    depreciationsByCategories,
    hasFunctionalDepreciation,
    hasExternalDepreciation,
    opinionOfSiteValue,

    // Reconciliation
    reconciliationPageData,
    reconciliation,
    aivReconciliationSummarizedValues,
    arvReconciliationSummarizedValues,
    opinionOfMarketValue,
    totalCostToCure,
    totalRenovationBudget,
    opinionOfAfterRepairValue,
    includeTotalCostToCure,
    includeTotalRenovationBudget,
    includeOpinionOfAfterRepairValue,
    estimatedROI: estimatedROI?.toJSON(),
    hasNegativeROI,
    valueType,

    // Prior sales
    priorSales,

    // Maps & coordinates
    compsForMaps,
    mapBoxCoordinates,
    mapStatistics,
    satelliteViewStaticUrl,

    // Charts
    siteValueChartParams,

    // Miscellaneous
    appraiserName,
    alerts,
    marketAggregateData,
    hasArvOption,
    signedAt,
    revisionType,
    CountyOrParish,
    CountyOrParishFIPS,
  };
};
